// @ts-ignore - React import for JSX compatibility
import React from 'react';
import { useContext, useEffect, useState } from 'react';
import { Routes as RouterRoutes, Route, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ThemeProvider, CssBaseline } from '@mui/material';
// import { Helmet, HelmetProvider } from 'react-helmet-async';

// Pages
import Home from '../pages/Home/Home';
import LetsStart from '../pages/LetsStart';
import Cookie from '../pages/Cookie';
import PatientSelection from '../pages/PatientSelection';
import Request from '../pages/Request';

// Components
import { Header, Footer, LoginCallback } from '../components';

// Styles & Themes
import { pitang, basic } from '../styles/themes';

// Hooks & Helpers
import { useTranslation, useHashFragment } from '../shared/hooks';
import { setCookie } from '../shared/helpers';

// Context
import { GeneralContext } from '../shared/contexts/GeneralContext.context';

// Routes
import {
  HOME,
  HOME_WITH_THEME,
  LETS_START,
  COOKIE,
  LOGIN,
  LOGOUT,
  LOGIN_CALLBACK,
  REQUEST,
  PATIENT_SELECTION,
  REFRESH_SESSION,
  HOME_WITH_THEME_LANG,
} from './routes-path';

const LOGIN_REDIRECT_GUARD_KEY = 'routeLoginRedirectAt';
const LOGIN_REDIRECT_GUARD_WINDOW_MS = 10000;

function LoginRoute() {
  const redirectToLoginOnce = (url = '/login') => {
    const now = Date.now();
    const lastRedirectAt = Number(
      sessionStorage.getItem(LOGIN_REDIRECT_GUARD_KEY)
    );
    const redirectedRecently =
      Number.isFinite(lastRedirectAt) &&
      now - lastRedirectAt < LOGIN_REDIRECT_GUARD_WINDOW_MS;

    if (redirectedRecently) {
      return;
    }

    sessionStorage.setItem(LOGIN_REDIRECT_GUARD_KEY, String(now));
    window.location.href = url;
  };

  useEffect(() => {
    if (navigator.onLine) {
      redirectToLoginOnce('/login');
    } else {
      console.log('LOGIN route - OFFLINE - not redirecting');
    }
  }, []);
  return <></>;
}

function LogoutRoute() {
  const { clearServiceWorkerCache } = useContext(GeneralContext);

  useEffect(() => {
    const performLogout = async () => {
      try {
        // Clear session storage
        sessionStorage.setItem('amosToken', '');
        sessionStorage.setItem('amosRefreshToken', '');
        sessionStorage.setItem('amosClient', '');
        setCookie('user', '');

        // Clear ALL user data from IndexedDB
        const userEmail = localStorage.getItem('lastUserEmail');
        if (userEmail) {
          console.log('🗑️ Clearing ALL IndexedDB data for user:', userEmail);

          const dbRequest = indexedDB.open('TeleconsultationDB');
          dbRequest.onsuccess = (event) => {
            const target = event.target as IDBOpenDBRequest;
            const db = target.result;

            // Clear ALL user-specific stores
            const storesToClear = [
              'userAuth',
              'amosTokens',
              'clientData',
              'videoRequests',
              'phoneRequests',
            ];

            storesToClear.forEach((storeName) => {
              if (db.objectStoreNames.contains(storeName)) {
                const transaction = db.transaction([storeName], 'readwrite');
                const store = transaction.objectStore(storeName);
                store.delete(userEmail);
                console.log(`   ✅ Cleared ${storeName} from IndexedDB`);
              }
            });

            console.log('✅ All user data cleared from IndexedDB');
          };

          localStorage.removeItem('lastUserEmail');
        }

        // Clear Service Worker caches
        await clearServiceWorkerCache();
        console.log('✅ Service Worker caches cleared');
      } catch (err) {
        console.error('❌ Error clearing SW caches:', err);
      }

      // Redirect to logout
      if (navigator.onLine) {
        console.log('LOGOUT route - ONLINE - redirecting to /logout');
        window.location.href = '/logout';
      } else {
        console.log(
          'LOGOUT route - OFFLINE - clearing session but not redirecting'
        );
      }
    };

    performLogout();
  }, [clearServiceWorkerCache]);

  return <></>;
}

function RefreshSessionRoute() {
  useEffect(() => {
    sessionStorage.setItem('amosToken', '');
    sessionStorage.setItem('amosRefreshToken', '');
    sessionStorage.setItem('amosClient', '');

    if (navigator.onLine) {
      console.log('REFRESH_SESSION route - ONLINE - redirecting to /logout');
      window.location.href = '/logout';
    } else {
      console.log(
        'REFRESH_SESSION route - OFFLINE - clearing session but not redirecting'
      );
    }
  }, []);

  return <></>;
}

function getOneTrustScriptId() {
  const hostname = window.location.hostname;

  // Get env vars (use import.meta.env for Vite)
  const DOMAIN_TRIPUK = import.meta.env.VITE_DOMAIN_TRIPUK;
  const DOMAIN_US = import.meta.env.VITE_DOMAIN_US;
  const DOMAIN_US_CHUBB = import.meta.env.VITE_DOMAIN_US_CHUBB;
  const DOMAIN_US_METLIFE = import.meta.env.VITE_DOMAIN_US_METLIFE;
  const DOMAIN_USTI = import.meta.env.VITE_DOMAIN_USTI;
  const DOMAIN_WWIIS = import.meta.env.VITE_DOMAIN_WWIIS;
  const DOMAIN_GENERIC = import.meta.env.VITE_DOMAIN_GENERIC;

  console.log('🌐 Current hostname:', hostname);

  if (hostname === DOMAIN_TRIPUK) {
    console.log(
      '🔑 Using OneTrust script ID for TRIPUK',
      import.meta.env.VITE_ONETRUST_COOKIE
    );
    return import.meta.env.VITE_ONETRUST_COOKIE;
  } else if (hostname === DOMAIN_US) {
    console.log(
      '🔑 Using OneTrust script ID for US',
      import.meta.env.VITE_ONETRUST_COOKIE_US
    );
    return import.meta.env.VITE_ONETRUST_COOKIE_US;
  } else if (hostname === DOMAIN_US_CHUBB) {
    console.log(
      '🔑 Using OneTrust script ID for US_CHUBB',
      import.meta.env.VITE_ONETRUST_COOKIE_CHUBB
    );
    return import.meta.env.VITE_ONETRUST_COOKIE_CHUBB;
  } else if (hostname === DOMAIN_US_METLIFE) {
    console.log(
      '🔑 Using OneTrust script ID for US_METLIFE',
      import.meta.env.VITE_ONETRUST_COOKIE_METLIFE
    );
    return import.meta.env.VITE_ONETRUST_COOKIE_METLIFE;
  } else if (hostname === DOMAIN_USTI) {
    console.log(
      '🔑 Using OneTrust script ID for USTI',
      import.meta.env.VITE_ONETRUST_COOKIE_USTI
    );
    return import.meta.env.VITE_ONETRUST_COOKIE_USTI;
  } else if (hostname === DOMAIN_WWIIS) {
    console.log(
      '🔑 Using OneTrust script ID for WWIIS',
      import.meta.env.VITE_ONETRUST_COOKIE_WWIIS
    );
    return import.meta.env.VITE_ONETRUST_COOKIE_WWIIS;
  } else if (hostname === DOMAIN_GENERIC) {
    console.log(
      '🔑 Using OneTrust script ID for GENERIC',
      import.meta.env.VITE_ONETRUST_COOKIE_GENERIC
    );
    return import.meta.env.VITE_ONETRUST_COOKIE_GENERIC;
  }

  console.log('🌐 Domain did not match any predefined domains, using GENERIC');
  return import.meta.env.VITE_ONETRUST_COOKIE_GENERIC;
}

/**
 * Main Routes Component
 */
function Routes() {
  useHashFragment();

  const { clearAmosToken, setUserData } = useContext(GeneralContext);
  const navigate = useNavigate();
  const { idiom } = useTranslation({});

  // Theme management - use lazy initialization to avoid setting state in effect
  const [theme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'pitang' ? pitang : basic;
  });

  useEffect(() => {
    // Inject OneTrust scripts into <head> in the correct order
    const scriptId = getOneTrustScriptId();

    // 1. Define OptanonWrapper FIRST (inline)
    if (!document.getElementById('onetrust-wrapper')) {
      const wrapperScript = document.createElement('script');
      wrapperScript.id = 'onetrust-wrapper';
      wrapperScript.type = 'text/javascript';
      wrapperScript.text = 'function OptanonWrapper() {}';
      document.head.appendChild(wrapperScript);
    }

    // 2. Then load the OneTrust SDK
    if (!document.getElementById('onetrust-sdk')) {
      const sdkScript = document.createElement('script');
      sdkScript.id = 'onetrust-sdk';
      sdkScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
      sdkScript.setAttribute('data-document-language', 'true');
      sdkScript.type = 'text/javascript';
      sdkScript.setAttribute('data-domain-script', scriptId);
      document.head.appendChild(sdkScript);
    }
  }, []);

  // Navigation type detection and auth handling
  useEffect(() => {
    const entries = performance?.getEntriesByType('navigation');
    const navigationEntry = entries?.[0] as
      | PerformanceNavigationTiming
      | undefined;
    const typeNavigation = navigationEntry?.type;

    // Check if this is actually a manual refresh (F5/Ctrl+R) or PWA reopen
    const wasOpen = sessionStorage.getItem('appWasOpen');

    if (typeNavigation === 'reload') {
      if (wasOpen && navigator.onLine) {
        // Manual refresh detected (F5/Ctrl+R)
        console.log('🔄 Manual refresh detected (F5/Ctrl+R) - clearing auth');
        setCookie('userAuth', '{}');
        setUserData({});
        clearAmosToken();

        const path = window.location.pathname + window.location.search;
        localStorage.setItem('oldPath', path);
        if (window.location.pathname !== '/login') {
          navigate('/login');
        }
      } else if (!wasOpen) {
        // PWA reopen
        console.log('📱 PWA reopen detected - preserving auth state');
      } else if (!navigator.onLine) {
        console.log('📴 Offline reload - preserving auth state');
      }
    } else {
      console.log(
        '✅ Normal navigation (not reload) - no auth clearing needed'
      );
    }

    // Mark app as open
    sessionStorage.setItem('appWasOpen', 'true');
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* OneTrust Cookie Consent */}
      {/* <Helmet> */}
      {/* <script
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        data-document-language="true"
        type="text/javascript"
        data-domain-script={getOneTrustScriptId()}
      />
      <script type="text/javascript">{`function OptanonWrapper() {}`}</script> */}
      {/* </Helmet> */}

      <Header />

      <RouterRoutes>
        {/* Login Routes */}
        <Route path={LOGIN} element={<LoginRoute />} />
        <Route
          path={`${LOGIN}?lang=${idiom.substr(0, 2)}`}
          element={<LoginRoute />}
        />

        {/* Logout Route */}
        <Route path={LOGOUT} element={<LogoutRoute />} />

        {/* Refresh Session Route */}
        <Route path={REFRESH_SESSION} element={<RefreshSessionRoute />} />

        {/* Login Callback */}
        <Route path={LOGIN_CALLBACK} element={<LoginCallback />} />

        {/* Home Routes */}
        <Route path={HOME} element={<Home />} />
        <Route path={HOME_WITH_THEME} element={<Home />} />
        <Route path={HOME_WITH_THEME_LANG} element={<Home />} />

        {/* App Routes */}
        <Route path={LETS_START} element={<LetsStart />} />
        <Route path={COOKIE} element={<Cookie />} />
        <Route path={PATIENT_SELECTION} element={<PatientSelection />} />
        <Route path={REQUEST} element={<Request />} />
      </RouterRoutes>

      <Space />
      <Footer />
    </ThemeProvider>
  );
}

const Space = styled.div`
  width: 100%;
  height: 20px;
`;

export default Routes;
